import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  ZoomOut, 
  Maximize2, 
  Play, 
  Pause,
  Grid,
  Image as ImageIcon,
  RotateCcw
} from 'lucide-react';

const PhotoGallery = ({ images, title }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(true);
  
  // Pan state for dragging when zoomed
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef(null);

  // Translated labels
  const texts = {
    pt: {
      viewAllPhotos: 'Ver todas as fotos',
      zoomIn: 'Ampliar',
      zoomOut: 'Reduzir',
      resetZoom: 'Resetar zoom',
      previousPhoto: 'Foto anterior',
      nextPhoto: 'Próxima foto',
      closeGallery: 'Fechar galeria',
      playSlideshow: 'Iniciar apresentação',
      pauseSlideshow: 'Pausar apresentação',
      toggleFullscreen: 'Tela cheia',
      toggleThumbnails: 'Mostrar/ocultar miniaturas',
      photo: 'Foto'
    },
    en: {
      viewAllPhotos: 'View all photos',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      resetZoom: 'Reset zoom',
      previousPhoto: 'Previous photo',
      nextPhoto: 'Next photo',
      closeGallery: 'Close gallery',
      playSlideshow: 'Start slideshow',
      pauseSlideshow: 'Pause slideshow',
      toggleFullscreen: 'Toggle fullscreen',
      toggleThumbnails: 'Toggle thumbnails',
      photo: 'Photo'
    }
  };
  const txt = texts[language];

  // Auto slideshow
  useEffect(() => {
    let interval;
    if (isPlaying && isOpen) {
      interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, isOpen, images.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
        case 'Escape':
          setIsOpen(false);
          break;
        case ' ':
          e.preventDefault();
          setIsPlaying(!isPlaying);
          break;
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
          e.preventDefault();
          handleZoomOut();
          break;
        case '0':
          e.preventDefault();
          resetZoom();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isPlaying, zoomLevel]);

  const openGallery = (index = 0) => {
    setCurrentIndex(index);
    setIsOpen(true);
    resetZoom();
  };

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    resetZoom();
  }, [images.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    resetZoom();
  }, [images.length]);

  const handleZoomIn = () => {
    if (zoomLevel < 3) {
      setZoomLevel((prev) => Math.min(prev + 0.5, 3));
      setIsZoomed(true);
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel > 1) {
      const newZoom = Math.max(zoomLevel - 0.5, 1);
      setZoomLevel(newZoom);
      if (newZoom <= 1) {
        setIsZoomed(false);
        setPanPosition({ x: 0, y: 0 });
      }
    }
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setIsZoomed(false);
    setPanPosition({ x: 0, y: 0 });
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // Pan/Drag handlers for zoomed images
  const handleMouseDown = (e) => {
    if (!isZoomed) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !isZoomed) return;
    
    const container = imageContainerRef.current;
    if (!container) return;
    
    // Calculate new position with bounds
    const maxPan = (zoomLevel - 1) * 200; // Adjust pan limits based on zoom
    let newX = e.clientX - dragStart.x;
    let newY = e.clientY - dragStart.y;
    
    // Clamp values
    newX = Math.max(-maxPan, Math.min(maxPan, newX));
    newY = Math.max(-maxPan, Math.min(maxPan, newY));
    
    setPanPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile pan
  const handleTouchStart = (e) => {
    if (!isZoomed || e.touches.length !== 1) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX - panPosition.x, y: touch.clientY - panPosition.y });
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !isZoomed || e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    const maxPan = (zoomLevel - 1) * 200;
    let newX = touch.clientX - dragStart.x;
    let newY = touch.clientY - dragStart.y;
    
    newX = Math.max(-maxPan, Math.min(maxPan, newX));
    newY = Math.max(-maxPan, Math.min(maxPan, newY));
    
    setPanPosition({ x: newX, y: newY });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Double click/tap to zoom
  const handleDoubleClick = () => {
    if (isZoomed) {
      resetZoom();
    } else {
      handleZoomIn();
    }
  };

  if (!images || images.length === 0) {
    return (
      <div className="aspect-[16/9] bg-gray-100 rounded-xl flex items-center justify-center">
        <ImageIcon className="w-16 h-16 text-gray-400" />
      </div>
    );
  }

  return (
    <>
      {/* Main Gallery Grid */}
      <div className="relative rounded-xl overflow-hidden">
        {images.length === 1 ? (
          // Single image
          <div 
            className="aspect-[16/9] md:aspect-[21/9] cursor-pointer group"
            onClick={() => openGallery(0)}
          >
            <img
              src={images[0].image_url}
              alt={`${title} - 1`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        ) : images.length <= 4 ? (
          // 2-4 images grid
          <div className="grid grid-cols-2 gap-2 aspect-[16/9] md:aspect-[21/9]">
            <div 
              className="relative cursor-pointer group row-span-2"
              onClick={() => openGallery(0)}
            >
              <img
                src={images[0].image_url}
                alt={`${title} - 1`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
            </div>
            <div className="grid grid-rows-2 gap-2">
              {images.slice(1, 3).map((img, idx) => (
                <div 
                  key={idx}
                  className="relative cursor-pointer group"
                  onClick={() => openGallery(idx + 1)}
                >
                  <img
                    src={img.image_url}
                    alt={`${title} - ${idx + 2}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  {idx === 1 && images.length > 3 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-xl font-bold">+{images.length - 3}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          // 5+ images grid (Airbnb style)
          <div className="grid grid-cols-4 grid-rows-2 gap-2 aspect-[16/9] md:aspect-[21/9]">
            <div 
              className="col-span-2 row-span-2 relative cursor-pointer group"
              onClick={() => openGallery(0)}
            >
              <img
                src={images[0].image_url}
                alt={`${title} - 1`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
            </div>
            {images.slice(1, 5).map((img, idx) => (
              <div 
                key={idx}
                className="relative cursor-pointer group"
                onClick={() => openGallery(idx + 1)}
              >
                <img
                  src={img.image_url}
                  alt={`${title} - ${idx + 2}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                {idx === 3 && images.length > 5 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-xl font-bold">+{images.length - 5}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* View All Photos Button */}
        {images.length > 1 && (
          <Button
            onClick={() => openGallery(0)}
            className="absolute bottom-4 right-4 bg-white/90 hover:bg-white text-gray-900 shadow-lg"
            size="sm"
            aria-label={`${txt.viewAllPhotos} (${images.length})`}
          >
            <Grid className="w-4 h-4 mr-2" />
            {txt.viewAllPhotos} ({images.length})
          </Button>
        )}
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent 
          className="max-w-[100vw] max-h-[100vh] w-screen h-screen p-0 bg-black/95 border-none"
          aria-label={`${txt.photo} ${currentIndex + 1} ${language === 'pt' ? 'de' : 'of'} ${images.length}`}
        >
          <div className="relative w-full h-full flex flex-col">
            {/* Top Controls */}
            <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium" aria-live="polite">
                  {currentIndex + 1} / {images.length}
                </span>
                <span className="text-white/70 text-sm hidden md:block">
                  {title}
                </span>
              </div>
              
              <div className="flex items-center gap-1 md:gap-2">
                {/* Zoom Out */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 1}
                  className="text-white hover:bg-white/20 disabled:opacity-40"
                  aria-label={txt.zoomOut}
                  data-testid="gallery-zoom-out"
                >
                  <ZoomOut className="w-5 h-5" />
                </Button>
                
                {/* Zoom Level */}
                <span className="text-white text-sm w-12 text-center" aria-label={`Zoom: ${Math.round(zoomLevel * 100)}%`}>
                  {Math.round(zoomLevel * 100)}%
                </span>
                
                {/* Zoom In */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 3}
                  className="text-white hover:bg-white/20 disabled:opacity-40"
                  aria-label={txt.zoomIn}
                  data-testid="gallery-zoom-in"
                >
                  <ZoomIn className="w-5 h-5" />
                </Button>

                {/* Reset Zoom */}
                {isZoomed && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={resetZoom}
                    className="text-white hover:bg-white/20"
                    aria-label={txt.resetZoom}
                    data-testid="gallery-reset-zoom"
                  >
                    <RotateCcw className="w-5 h-5" />
                  </Button>
                )}

                {/* Slideshow Toggle */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="text-white hover:bg-white/20"
                  aria-label={isPlaying ? txt.pauseSlideshow : txt.playSlideshow}
                  data-testid="gallery-slideshow-toggle"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </Button>

                {/* Fullscreen Toggle */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleFullscreen}
                  className="text-white hover:bg-white/20 hidden md:flex"
                  aria-label={txt.toggleFullscreen}
                  data-testid="gallery-fullscreen-toggle"
                >
                  <Maximize2 className="w-5 h-5" />
                </Button>

                {/* Thumbnails Toggle */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowThumbnails(!showThumbnails)}
                  className="text-white hover:bg-white/20"
                  aria-label={txt.toggleThumbnails}
                  aria-pressed={showThumbnails}
                  data-testid="gallery-thumbnails-toggle"
                >
                  <Grid className="w-5 h-5" />
                </Button>

                {/* Close Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-white/20"
                  aria-label={txt.closeGallery}
                  data-testid="gallery-close"
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>
            </div>

            {/* Main Image Area */}
            <div 
              ref={imageContainerRef}
              className="flex-1 flex items-center justify-center overflow-hidden relative select-none"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onDoubleClick={handleDoubleClick}
              style={{
                cursor: isDragging ? 'grabbing' : isZoomed ? 'grab' : 'zoom-in'
              }}
            >
              {/* Previous Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                className="absolute left-4 z-40 text-white hover:bg-white/20 h-12 w-12"
                aria-label={txt.previousPhoto}
                data-testid="gallery-prev"
              >
                <ChevronLeft className="w-8 h-8" />
              </Button>

              {/* Image */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center justify-center w-full h-full p-16"
                >
                  <img
                    src={images[currentIndex].image_url}
                    alt={`${title} - ${txt.photo} ${currentIndex + 1}`}
                    className="max-w-full max-h-full object-contain transition-transform duration-200"
                    style={{
                      transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                    }}
                    draggable={false}
                  />
                </motion.div>
              </AnimatePresence>

              {/* Next Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="absolute right-4 z-40 text-white hover:bg-white/20 h-12 w-12"
                aria-label={txt.nextPhoto}
                data-testid="gallery-next"
              >
                <ChevronRight className="w-8 h-8" />
              </Button>
            </div>

            {/* Thumbnails */}
            <AnimatePresence>
              {showThumbnails && (
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 50 }}
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4"
                >
                  <div className="flex gap-2 overflow-x-auto pb-2 justify-center" role="tablist">
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setCurrentIndex(idx);
                          resetZoom();
                        }}
                        className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          idx === currentIndex 
                            ? 'border-white ring-2 ring-cyan-400' 
                            : 'border-transparent opacity-60 hover:opacity-100'
                        }`}
                        role="tab"
                        aria-selected={idx === currentIndex}
                        aria-label={`${txt.photo} ${idx + 1}`}
                      >
                        <img
                          src={img.image_url}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Slideshow Progress Bar */}
            {isPlaying && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                <motion.div
                  key={currentIndex}
                  className="h-full bg-cyan-400"
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 3, ease: 'linear' }}
                />
              </div>
            )}

            {/* Pan hint when zoomed */}
            {isZoomed && !isDragging && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-2 rounded-full pointer-events-none"
              >
                {language === 'pt' ? 'Arraste para mover a imagem' : 'Drag to pan the image'}
              </motion.div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PhotoGallery;
